const DIGITS_ONLY = /^\d+$/;

function assertMinorUnits(value: string) {
    if (!DIGITS_ONLY.test(value)) {
        throw new Error("Minor-unit amount must contain digits only");
    }
}

function assertDecimalScale(value: number) {
    if (!Number.isInteger(value) || value < 0) {
        throw new Error("Decimal scale must be a non-negative integer");
    }
}

function numberFormatter(locale: string, useGrouping: boolean) {
    return new Intl.NumberFormat(locale, {
        useGrouping,
        maximumFractionDigits: 0,
    });
}

function localeDecimalSeparator(locale: string): string {
    return (
        new Intl.NumberFormat(locale)
            .formatToParts(1.1)
            .find((part) => part.type === "decimal")?.value ?? "."
    );
}

function localizeDigits(value: string, locale: string): string {
    const formatter = numberFormatter(locale, false);
    const digits = Array.from({ length: 10 }, (_, digit) =>
        formatter.format(digit),
    );
    return value.replace(/\d/g, (digit) => digits[Number(digit)] ?? digit);
}

function normalizeDigits(value: string): string {
    return value.replace(
        /[\u0660-\u0669\u06f0-\u06f9\u0966-\u096f]/g,
        (digit) => {
            const code = digit.charCodeAt(0);
            if (code >= 0x0660 && code <= 0x0669) return String(code - 0x0660);
            if (code >= 0x06f0 && code <= 0x06f9) return String(code - 0x06f0);
            return String(code - 0x0966);
        },
    );
}

function escapeCharacterClass(value: string): string {
    return value.replace(/[\\\]\-^]/g, "\\$&");
}

export function minorUnitsToDecimal(
    minorUnits: string,
    decimalScale: number,
    locale = "en",
): string {
    assertMinorUnits(minorUnits);
    assertDecimalScale(decimalScale);

    const normalized = minorUnits.replace(/^0+(?=\d)/, "");
    if (decimalScale === 0) return localizeDigits(normalized, locale);

    const padded = normalized.padStart(decimalScale + 1, "0");
    const whole = padded.slice(0, -decimalScale);
    const fraction = padded.slice(-decimalScale).replace(/0+$/, "");
    const localizedWhole = localizeDigits(whole, locale);
    return fraction
        ? `${localizedWhole}${localeDecimalSeparator(locale)}${localizeDigits(fraction, locale)}`
        : localizedWhole;
}

export function formatMinorUnits(
    minorUnits: string,
    decimalScale: number,
    locale = "en",
): string {
    const decimal = minorUnitsToDecimal(minorUnits, decimalScale, "en");
    const [whole = "0", fraction] = decimal.split(".");
    const groupedWhole = numberFormatter(locale, true).format(BigInt(whole));
    return fraction
        ? `${groupedWhole}${localeDecimalSeparator(locale)}${localizeDigits(fraction, locale)}`
        : groupedWhole;
}

/**
 * Compact balance formatting for the HUD without converting exact token
 * amounts to a floating-point number.
 */
export function formatHudMinorUnits(
    minorUnits: string,
    decimalScale: number,
    locale = "en",
): string {
    assertMinorUnits(minorUnits);
    assertDecimalScale(decimalScale);

    const amount = BigInt(minorUnits);
    if (amount === 0n) return formatMinorUnits("0", 0, locale);

    if (decimalScale > 3 && amount < 10n ** BigInt(decimalScale - 3)) {
        return `<${minorUnitsToDecimal("1", 3, locale)}`;
    }

    const one = 10n ** BigInt(decimalScale);
    const maximumFractionDigits = amount < one ? 3 : 0;
    const displayedScale = Math.min(decimalScale, maximumFractionDigits);
    const discardedDigits = decimalScale - displayedScale;

    if (discardedDigits === 0) {
        return formatMinorUnits(minorUnits, displayedScale, locale);
    }

    const divisor = 10n ** BigInt(discardedDigits);
    const roundedAmount = (amount + divisor / 2n) / divisor;
    return formatMinorUnits(roundedAmount.toString(), displayedScale, locale);
}

export function decimalToMinorUnits(
    value: string,
    decimalScale: number,
    locale = "en",
): string | null {
    const trimmed = normalizeDigits(value.trim());
    if (!trimmed || !Number.isInteger(decimalScale) || decimalScale < 0) {
        return null;
    }

    const localeSeparator = localeDecimalSeparator(locale);
    const acceptedSeparators = new Set([".", localeSeparator]);
    const separatorPattern = [...acceptedSeparators]
        .map(escapeCharacterClass)
        .join("");
    const match = new RegExp(`^(\\d+)(?:[${separatorPattern}](\\d*))?$`).exec(
        trimmed,
    );
    if (!match) return null;

    const whole = match[1] ?? "";
    const fraction = match[2] ?? "";
    if (fraction.length > decimalScale) return null;
    if (decimalScale === 0 && match[2] !== undefined) return null;

    const combined = `${whole}${fraction.padEnd(decimalScale, "0")}`;
    return BigInt(combined || "0").toString();
}

export function isFriendlyMainnetTonAddress(value: string): boolean {
    return /^(?:EQ|UQ)[A-Za-z0-9_-]{46}$/.test(value.trim());
}

export const CURRENCY_DECIMAL_SCALE: Readonly<Record<string, number>> = {
    BEACH_COINS: 0,
    GRAM: 9,
    NOT: 9,
    USDT: 6,
};

function formatRoundedMinorUnits(
    amount: bigint,
    decimalScale: number,
    maximumFractionDigits: number,
    locale: string,
): string {
    const displayedScale = Math.min(decimalScale, maximumFractionDigits);
    const discardedDigits = decimalScale - displayedScale;
    if (discardedDigits === 0) {
        return formatMinorUnits(amount.toString(), displayedScale, locale);
    }

    const divisor = 10n ** BigInt(discardedDigits);
    const roundedAmount = (amount + divisor / 2n) / divisor;
    return formatMinorUnits(roundedAmount.toString(), displayedScale, locale);
}

/**
 * Compact one friend-steal transfer without converting authoritative minor
 * units to floating point. Values below 1,000 retain up to three fractional
 * digits; larger values use an exact rounded K/M/B suffix.
 */
export function formatCompactCurrencyMinorUnits(
    amount: string,
    currencyCode: string,
    locale: string,
): string {
    assertMinorUnits(amount);
    const decimalScale = CURRENCY_DECIMAL_SCALE[currencyCode] ?? 0;
    const minorUnitAmount = BigInt(amount);
    const oneCurrencyUnit = 10n ** BigInt(decimalScale);
    const compactUnits = [
        { threshold: 1_000_000_000n, suffix: "B" },
        { threshold: 1_000_000n, suffix: "M" },
        { threshold: 1_000n, suffix: "K" },
    ] as const;

    for (const unit of compactUnits) {
        const compactMinorUnits = oneCurrencyUnit * unit.threshold;
        if (minorUnitAmount < compactMinorUnits) continue;

        const roundedTenths =
            (minorUnitAmount * 10n + compactMinorUnits / 2n) /
            compactMinorUnits;
        const whole = roundedTenths / 10n;
        const fraction = roundedTenths % 10n;
        const localizedWhole = numberFormatter(locale, true).format(whole);
        const compactValue =
            fraction === 0n
                ? localizedWhole
                : `${localizedWhole}${localeDecimalSeparator(locale)}${localizeDigits(fraction.toString(), locale)}`;
        return `${compactValue}${unit.suffix}`;
    }

    if (
        decimalScale > 3 &&
        minorUnitAmount > 0n &&
        minorUnitAmount < 10n ** BigInt(decimalScale - 3)
    ) {
        return `<${minorUnitsToDecimal("1", 3, locale)}`;
    }

    return formatRoundedMinorUnits(
        minorUnitAmount,
        decimalScale,
        3,
        locale,
    );
}

/** Format one API currency transfer while retaining exact minor-unit math. */
export function formatCurrencyMinorUnits(
    amount: string,
    currencyCode: string,
    locale: string,
): string {
    const decimalScale = CURRENCY_DECIMAL_SCALE[currencyCode] ?? 0;
    return `${formatMinorUnits(amount, decimalScale, locale)} ${currencyCode}`;
}
